// Localização: frontend/src/components/Financeiro/FormContaReceber.jsx
// Formulário para criar/editar contas a receber

import React, { useState, useEffect, useRef } from 'react';
import Modal from '../UI/Modal';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import DropdownMenu from '../UI/DropdownMenu';
import ParcelasManager from './ParcelasManager';
import { useCategorias, useSubcategorias, useContasBancarias } from '../../hooks/useFinanceiro';
import { usePacientes } from '../../hooks/usePacientes';
import api from '../../api/api';
import { DollarSign, Calendar, FileText, User, CreditCard, Users, ChevronDown, Tag, Repeat, Receipt, CalendarClock, RotateCw, CheckCircle, Clock, XCircle, UserCircle, UserPlus, UserX } from 'lucide-react';
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils';

// Tipos de lançamento com ícones
const TIPOS_LANCAMENTO = [
    { value: 'unica', label: 'Única', icon: Receipt },
    { value: 'parcelada', label: 'Parcelada', icon: CalendarClock },
    { value: 'recorrente_fixo', label: 'Recorrente Fixo (Mensalidade)', icon: Repeat },
    { value: 'recorrente_variavel', label: 'Recorrente Variável', icon: RotateCw },
];

// Status com ícones
const STATUS_CONTA = [
    { value: 'Pendente', label: 'Pendente', icon: Clock },
    { value: 'Vencido', label: 'Vencido', icon: XCircle },
    { value: 'Cancelado', label: 'Cancelado', icon: XCircle },
];

const FormContaReceber = ({ isOpen, onClose, conta, onSave, pacienteId, pacienteData }) => {
    const [formData, setFormData] = useState({
        conta_bancaria_id: '',
        categoria_id: '',
        subcategoria_id: '',
        descricao: '',
        valor_total: '',
        data_vencimento: '',
        data_competencia: '',
        cliente_fornecedor: '',
        documento: '',
        observacoes: '',
        paciente_id: '',
        usuario_id: '',
        tipo_lancamento: 'unica',
        lancar_quitada: false,
        status: 'Pendente'
    });

    const [parcelas, setParcelas] = useState([]);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [usuarios, setUsuarios] = useState([]);
    const [loadingUsuarios, setLoadingUsuarios] = useState(false);

    // Hooks para buscar dados relacionados
    const { categorias, fetchCategorias, loading: loadingCategorias } = useCategorias();
    const { subcategorias, fetchSubcategorias, loading: loadingSubcategorias } = useSubcategorias();
    const { contas, fetchContas, loading: loadingContas } = useContasBancarias();
    const { pacientes, fetchPacientes, isLoading: loadingPacientes } = usePacientes();

    // Função para buscar usuários
    const fetchUsuarios = React.useCallback(async () => {
        setLoadingUsuarios(true);
        try {
            const response = await api.get('/usuarios');
            setUsuarios(response.data.data || response.data || []);
        } catch (error) {
            console.error('Erro ao buscar usuários:', error);
        } finally {
            setLoadingUsuarios(false);
        }
    }, []);

    // Refs para evitar múltiplas chamadas e armazenar funções estáveis
    const hasLoadedDataRef = useRef(false);
    const lastCategoriaIdRef = useRef(null);
    const isInitializingRef = useRef(false);
    const abortControllerRef = useRef(null);
    
    // Refs para armazenar as funções de fetch (evita recriação)
    const fetchCategoriasRef = useRef(fetchCategorias);
    const fetchContasRef = useRef(fetchContas);
    const fetchPacientesRef = useRef(fetchPacientes);
    const fetchUsuariosRef = useRef(fetchUsuarios);
    
    // Atualizar refs quando as funções mudarem
    useEffect(() => {
        fetchCategoriasRef.current = fetchCategorias;
        fetchContasRef.current = fetchContas;
        fetchPacientesRef.current = fetchPacientes;
        fetchUsuariosRef.current = fetchUsuarios;
    }, [fetchCategorias, fetchContas, fetchPacientes, fetchUsuarios]);

    // Carregar dados ao abrir o modal
    useEffect(() => {
        // Não fazer nada se já estiver inicializando ou se o modal estiver fechado
        if (!isOpen || isInitializingRef.current) {
            return;
        }
        
        // Cancelar requisições anteriores se existirem
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Criar novo AbortController para esta sessão do modal
        abortControllerRef.current = new AbortController();
        const currentAbortController = abortControllerRef.current;
        
        // Marcar como inicializando para evitar múltiplas execuções simultâneas
        if (!hasLoadedDataRef.current) {
            isInitializingRef.current = true;
            
            // Carregar dados apenas uma vez quando o modal abrir
            Promise.all([
                fetchCategoriasRef.current({ tipo: 'Receita' }).catch(err => {
                    // Ignorar erros de abort
                    if (err.name === 'AbortError' || currentAbortController.signal.aborted) {
                        return null;
                    }
                    console.error('[FormContaReceber] Erro ao carregar categorias:', err);
                    return null;
                }),
                fetchContasRef.current().catch(err => {
                    if (err.name === 'AbortError' || currentAbortController.signal.aborted) {
                        return null;
                    }
                    console.error('[FormContaReceber] Erro ao carregar contas:', err);
                    return null;
                }),
                fetchPacientesRef.current().catch(err => {
                    if (err.name === 'AbortError' || currentAbortController.signal.aborted) {
                        return null;
                    }
                    console.error('[FormContaReceber] Erro ao carregar pacientes:', err);
                    return null;
                }),
                fetchUsuariosRef.current().catch(err => {
                    if (err.name === 'AbortError' || currentAbortController.signal.aborted) {
                        return null;
                    }
                    console.error('[FormContaReceber] Erro ao carregar usuários:', err);
                    return null;
                })
            ]).finally(() => {
                // Só atualizar se não foi abortado
                if (!currentAbortController.signal.aborted) {
                    hasLoadedDataRef.current = true;
                    isInitializingRef.current = false;
                }
            });
        }

        // Configurar dados do formulário
        if (conta) {
            const dataCompetencia = conta.data_competencia || conta.data_vencimento || '';

            setFormData({
                categoria_id: conta.categoria_id || '',
                subcategoria_id: conta.subcategoria_id || '',
                descricao: conta.descricao || '',
                valor_total: conta.valor_total || '',
                data_vencimento: conta.data_vencimento || '',
                data_competencia: dataCompetencia,
                conta_bancaria_id: conta.conta_bancaria_id || '',
                cliente_fornecedor: conta.cliente_fornecedor || '',
                documento: conta.documento || '',
                observacoes: conta.observacoes || '',
                paciente_id: conta.paciente_id || '',
                usuario_id: conta.usuario_id || '',
                tipo_lancamento: conta.tipo_lancamento || 'unica',
                lancar_quitada: conta.status === 'Recebido',
                status: conta.status || 'Pendente'
            });
            
            // Atualizar ref da categoria para carregar subcategorias
            lastCategoriaIdRef.current = conta.categoria_id || null;
        } else {
            // Ao criar nova conta, definir data_competencia como data atual
            const hoje = new Date().toISOString().split('T')[0];
            
            // Se for conta de paciente, padrão é recorrente_fixo (mensalidade)
            // Caso contrário, padrão é unica
            const tipoLancamentoPadrao = pacienteId ? 'recorrente_fixo' : 'unica';
            
            // Pré-preencher com dados do paciente se disponível
            const initialData = {
                categoria_id: '',
                subcategoria_id: '',
                descricao: '',
                valor_total: '',
                data_vencimento: '',
                data_competencia: hoje,
                conta_bancaria_id: '',
                cliente_fornecedor: pacienteData?.nome_completo || '',
                documento: '',
                observacoes: '',
                paciente_id: pacienteId || '',
                usuario_id: '',
                tipo_lancamento: tipoLancamentoPadrao,
                lancar_quitada: false
            };
            
            setFormData(initialData);
            setParcelas([]);
            lastCategoriaIdRef.current = null;
        }
        setErrors({});
        
        // Cleanup: cancelar requisições quando o modal fechar ou componente desmontar
        return () => {
            // Cancelar requisições pendentes sempre
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            
            // Resetar flags apenas quando o modal fechar (não quando mudar conta/paciente)
            // Usar uma verificação mais robusta: se isOpen mudou para false, resetar
            // Mas não resetar se o modal ainda estiver aberto (mudança de conta/paciente)
        };
    }, [isOpen, conta, pacienteId, pacienteData]); // Removidas funções das dependências para evitar loops
    
    // Cleanup separado para quando o modal fechar
    useEffect(() => {
        if (!isOpen) {
            // Resetar flags quando o modal fechar
            hasLoadedDataRef.current = false;
            lastCategoriaIdRef.current = null;
            isInitializingRef.current = false;
            
            // Cancelar requisições pendentes
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        }
    }, [isOpen]);

    // Ref para armazenar função de fetch de subcategorias
    const fetchSubcategoriasRef = useRef(fetchSubcategorias);
    
    // Atualizar ref quando a função mudar
    useEffect(() => {
        fetchSubcategoriasRef.current = fetchSubcategorias;
    }, [fetchSubcategorias]);
    
    // Carregar subcategorias quando categoria mudar (apenas se realmente mudou)
    useEffect(() => {
        // Só buscar se a categoria realmente mudou e o modal está aberto
        if (!isOpen) {
            return; // Não fazer nada se o modal estiver fechado
        }
        
        const currentCategoriaId = formData.categoria_id || null;
        const previousCategoriaId = lastCategoriaIdRef.current;
        
        // Só buscar se a categoria realmente mudou
        if (currentCategoriaId !== previousCategoriaId) {
            lastCategoriaIdRef.current = currentCategoriaId;
            
            if (currentCategoriaId) {
                fetchSubcategoriasRef.current({ categoria_id: currentCategoriaId }).catch(err => 
                    console.error('[FormContaReceber] Erro ao carregar subcategorias:', err)
                );
            } else {
                // Limpar subcategorias apenas se havia uma categoria antes
                if (previousCategoriaId !== null) {
                    fetchSubcategoriasRef.current({ categoria_id: null }).catch(err => 
                        console.error('[FormContaReceber] Erro ao limpar subcategorias:', err)
                    );
                }
            }
        }
    }, [isOpen, formData.categoria_id]); // Removido fetchSubcategorias das dependências

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({ ...prev, [name]: newValue }));

        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        // Se mudou a categoria, limpar subcategoria
        if (name === 'categoria_id') {
            setFormData(prev => ({ ...prev, subcategoria_id: '' }));
        }

        // Se mudou tipo_lancamento para não-parcelada, limpar parcelas
        if (name === 'tipo_lancamento' && value !== 'parcelada') {
            setParcelas([]);
        }

        // Se marcou lancar_quitada, data_competencia = data_vencimento
        if (name === 'lancar_quitada' && checked && formData.data_vencimento) {
            setFormData(prev => ({ ...prev, data_competencia: prev.data_vencimento }));
        }
    };

    // Handler para campos que usam DropdownMenu
    const handleDropdownChange = (name, value) => {
        setFormData(prev => {
            const newData = { ...prev, [name]: value };
            // Se mudou a categoria, limpar subcategoria
            if (name === 'categoria_id') {
                newData.subcategoria_id = '';
            }
            // Se mudou tipo_lancamento para não-parcelada, limpar parcelas
            if (name === 'tipo_lancamento' && value !== 'parcelada') {
                setParcelas([]);
            }
            return newData;
        });
        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.descricao?.trim()) {
            newErrors.descricao = 'Descrição é obrigatória';
        }

        if (!formData.valor_total || parseFloat(formData.valor_total) <= 0) {
            newErrors.valor_total = 'Valor total deve ser maior que zero';
        }

        if (!formData.data_vencimento) {
            newErrors.data_vencimento = 'Data de vencimento é obrigatória';
        }

        if (!formData.data_competencia) {
            newErrors.data_competencia = 'Data de competência é obrigatória';
        }

        if (!formData.categoria_id) {
            newErrors.categoria_id = 'Categoria é obrigatória';
        }

        if (formData.lancar_quitada && !formData.conta_bancaria_id) {
            newErrors.conta_bancaria_id = 'Conta bancária é obrigatória para lançamento quitado';
        }

        if (formData.tipo_lancamento === 'parcelada') {
            if (parcelas.length === 0) {
                newErrors.parcelas = 'Configure as parcelas antes de salvar';
            } else {
                // Validar soma das parcelas
                const soma = parcelas.reduce((acc, p) => acc + parseFloat(p.valor_parcela || 0), 0);
                const diferenca = Math.abs(parseFloat(formData.valor_total) - soma);
                if (diferenca > 0.01) {
                    newErrors.parcelas = 'A soma das parcelas deve ser igual ao valor total';
                }

                // Validar cada parcela
                for (let i = 0; i < parcelas.length; i++) {
                    if (!parcelas[i].data_vencimento) {
                        newErrors.parcelas = `Parcela ${i + 1} sem data de vencimento`;
                        break;
                    }
                    if (!parcelas[i].valor_parcela || parseFloat(parcelas[i].valor_parcela) <= 0) {
                        newErrors.parcelas = `Parcela ${i + 1} com valor inválido`;
                        break;
                    }
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const dataToSave = {
                descricao: formData.descricao,
                valor_total: parseFloat(formData.valor_total),
                data_vencimento: formData.data_vencimento,
                data_competencia: formData.data_competencia,
                categoria_id: formData.categoria_id,
                subcategoria_id: formData.subcategoria_id || null,
                conta_bancaria_id: formData.conta_bancaria_id || null,
                cliente_fornecedor: formData.cliente_fornecedor || null,
                documento: formData.documento || null,
                observacoes: formData.observacoes || null,
                paciente_id: formData.paciente_id || null,
                usuario_id: formData.usuario_id || null,
                tipo_lancamento: formData.tipo_lancamento,
                lancar_quitada: formData.lancar_quitada
            };

            // Adicionar parcelas se for parcelada
            if (formData.tipo_lancamento === 'parcelada') {
                dataToSave.parcelas = parcelas;
            }

            // Adicionar status apenas se estiver editando (permite reativar contas canceladas)
            if (conta && formData.status) {
                dataToSave.status = formData.status;
            }

            await onSave(dataToSave);
            onClose();
        } catch (error) {
            console.error('Erro ao salvar conta a receber:', error);
            setErrors({ submit: 'Erro ao salvar. Tente novamente.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const mostrarParcelas = formData.tipo_lancamento === 'parcelada';
    const mostrarContaBancaria = formData.tipo_lancamento !== 'parcelada';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={conta ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
            size="xl"
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Descrição */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="w-4 h-4 inline mr-1" />
                        Descrição <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="descricao"
                        value={formData.descricao}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.descricao ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="Ex: Consulta paciente João"
                    />
                    {errors.descricao && (
                        <p className="mt-1 text-sm text-red-500">{errors.descricao}</p>
                    )}
                </div>

                {/* Valor Total e Data de Vencimento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <DollarSign className="w-4 h-4 inline mr-1" />
                            Valor Total <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="number"
                            name="valor_total"
                            value={formData.valor_total}
                            onChange={handleChange}
                            step="0.01"
                            min="0.01"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.valor_total ? 'border-red-500' : 'border-gray-300'
                                }`}
                            placeholder="0.00"
                        />
                        {errors.valor_total && (
                            <p className="mt-1 text-sm text-red-500">{errors.valor_total}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            <Calendar className="w-4 h-4 inline mr-1" />
                            Data de Vencimento <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="data_vencimento"
                            value={formData.data_vencimento}
                            onChange={handleChange}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_vencimento ? 'border-red-500' : 'border-gray-300'
                                }`}
                        />
                        {errors.data_vencimento && (
                            <p className="mt-1 text-sm text-red-500">{errors.data_vencimento}</p>
                        )}
                    </div>
                </div>

                {/* Data de Competência */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Data de Competência <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="data_competencia"
                        value={formData.data_competencia}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_competencia ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.data_competencia && (
                        <p className="mt-1 text-sm text-red-500">{errors.data_competencia}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                        Data para cálculo do DRE (Demonstração do Resultado do Exercício)
                    </p>
                </div>

                {/* Categoria e Subcategoria - Usando DropdownMenu */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Tag size={16} className="text-gray-500" />
                            Categoria <span className="text-red-500">*</span>
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className={`w-full flex items-center justify-between gap-2 p-2 border rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                        errors.categoria_id ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    disabled={loadingCategorias}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingCategorias ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const categoriaSelecionada = categorias?.find(c => String(c.id) === String(formData.categoria_id));
                                            return (
                                                <>
                                                    <Tag size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {categoriaSelecionada?.nome || "Selecione uma categoria..."}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={loadingCategorias ? 
                                [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                                categorias?.map(cat => ({
                                    id: String(cat.id),
                                    label: cat.nome,
                                    icon: Tag,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('categoria_id', String(cat.id))
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                        {errors.categoria_id && (
                            <p className="mt-1 text-sm text-red-500">{errors.categoria_id}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Tag size={16} className="text-gray-500" />
                            Subcategoria
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={!formData.categoria_id || loadingSubcategorias}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingSubcategorias ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const subcategoriaSelecionada = subcategorias?.find(s => String(s.id) === String(formData.subcategoria_id));
                                            return (
                                                <>
                                                    <Tag size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {subcategoriaSelecionada?.nome || "Selecione uma subcategoria..."}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={!formData.categoria_id || loadingSubcategorias ? 
                                [{ id: 'disabled', label: formData.categoria_id ? 'Carregando...' : 'Selecione uma categoria primeiro', disabled: true }] :
                                subcategorias?.map(sub => ({
                                    id: String(sub.id),
                                    label: sub.nome,
                                    icon: Tag,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('subcategoria_id', String(sub.id))
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Tipo de Lançamento - Usando DropdownMenu */}
                <div>
                    <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                        <Repeat size={16} className="text-gray-500" />
                        Tipo de Lançamento <span className="text-red-500">*</span>
                    </label>
                    <DropdownMenu
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => e.preventDefault()}
                                className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={!!conta}
                            >
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const tipoSelecionado = TIPOS_LANCAMENTO.find(t => t.value === formData.tipo_lancamento);
                                        const Icon = tipoSelecionado?.icon || Repeat;
                                        return (
                                            <>
                                                <Icon size={16} className="text-gray-500" />
                                                <span className="text-gray-700">
                                                    {tipoSelecionado?.label || "Selecione o tipo"}
                                                </span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <ChevronDown size={16} className="text-gray-400" />
                            </button>
                        }
                        items={TIPOS_LANCAMENTO.map(t => ({
                            id: t.value,
                            label: t.label,
                            icon: t.icon,
                            iconColor: 'text-gray-600',
                            onClick: () => handleDropdownChange('tipo_lancamento', t.value),
                            disabled: !!conta
                        }))}
                        position="bottom-left"
                        className="w-full"
                    />
                    {conta && (
                        <p className="mt-1 text-xs text-gray-500">
                            Tipo de lançamento não pode ser alterado após criação
                        </p>
                    )}
                </div>

                {/* Parcelas (apenas se tipo = parcelada) */}
                {mostrarParcelas && (
                    <div>
                        <ParcelasManager
                            valorTotal={formData.valor_total}
                            parcelas={parcelas}
                            onChange={setParcelas}
                            disabled={!!conta}
                        />
                        {errors.parcelas && (
                            <p className="mt-2 text-sm text-red-600">{errors.parcelas}</p>
                        )}
                    </div>
                )}

                {/* Conta Bancária (apenas se não for parcelada) - Usando DropdownMenu */}
                {mostrarContaBancaria && (
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <CreditCard size={16} className="text-gray-500" />
                            Conta Bancária {formData.lancar_quitada && <span className="text-red-500">*</span>}
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className={`w-full flex items-center justify-between gap-2 p-2 border rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition ${
                                        errors.conta_bancaria_id ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    disabled={loadingContas}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingContas ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const contaSelecionada = contas?.find(c => String(c.id) === String(formData.conta_bancaria_id));
                                            return (
                                                <>
                                                    <CreditCard size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {contaSelecionada ? `${contaSelecionada.nome} - ${contaSelecionada.banco}` : "Selecione uma conta bancária..."}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={loadingContas ? 
                                [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                                contas?.map(conta => ({
                                    id: String(conta.id),
                                    label: `${conta.nome} - ${conta.banco}`,
                                    icon: CreditCard,
                                    iconColor: 'text-gray-600',
                                    onClick: () => handleDropdownChange('conta_bancaria_id', String(conta.id))
                                })) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                        {errors.conta_bancaria_id && (
                            <p className="mt-1 text-sm text-red-500">{errors.conta_bancaria_id}</p>
                        )}
                    </div>
                )}

                {/* Lançar como Quitada */}
                {!conta && (
                    <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <input
                            type="checkbox"
                            id="lancar_quitada"
                            name="lancar_quitada"
                            checked={formData.lancar_quitada}
                            onChange={handleChange}
                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <label htmlFor="lancar_quitada" className="text-sm font-medium text-gray-700 cursor-pointer">
                            Lançar como quitada (já recebida)
                        </label>
                    </div>
                )}

                {/* Paciente e Usuário - Usando DropdownMenu */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <User size={16} className="text-gray-500" />
                            Paciente
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loadingPacientes || (pacienteId !== undefined && pacienteId !== null && pacienteId !== '')}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingPacientes ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const pacienteSelecionado = pacientes?.find(p => String(p.id) === String(formData.paciente_id));
                                            
                                            if (!pacienteSelecionado) {
                                                return (
                                                    <>
                                                        <UserPlus size={20} className="text-gray-400" />
                                                        <span className="text-gray-700">
                                                            {pacienteId && pacienteData ? pacienteData.nome_completo : 'Selecione um paciente...'}
                                                        </span>
                                                    </>
                                                );
                                            }
                                            
                                            const temFoto = pacienteSelecionado?.fotoURL;
                                            const fotoUrl = temFoto ? getPacienteFotoUrl(pacienteSelecionado.fotoURL) : null;
                                            
                                            return (
                                                <>
                                                    {temFoto && fotoUrl ? (
                                                        <img
                                                            src={fotoUrl}
                                                            alt={pacienteSelecionado.nome_completo}
                                                            className="h-6 w-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                                                if (iconElement) iconElement.style.display = 'block';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <UserCircle 
                                                        size={20} 
                                                        className="text-gray-400 fallback-icon" 
                                                        style={{ display: (temFoto && fotoUrl) ? 'none' : 'block' }} 
                                                    />
                                                    <span className="text-gray-700">
                                                        {pacienteSelecionado.nome_completo}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={loadingPacientes ? 
                                [{ id: 'loading', label: 'Carregando...', disabled: true }] :
                                pacientes?.map((p) => {
                                    const temFoto = p.fotoURL;
                                    const fotoUrl = temFoto ? getPacienteFotoUrl(p.fotoURL) : null;
                                    return {
                                        id: String(p.id),
                                        label: p.nome_completo,
                                        ...(temFoto && fotoUrl ? { image: fotoUrl, imageAlt: p.nome_completo } : { icon: UserCircle, iconColor: 'text-gray-400' }),
                                        onClick: () => handleDropdownChange('paciente_id', String(p.id))
                                    };
                                }) || []
                            }
                            position="bottom-left"
                            className="w-full"
                        />
                        {pacienteId && pacienteData && (
                            <p className="mt-1 text-xs text-emerald-600">
                                ✓ Paciente pré-selecionado: {pacienteData.nome_completo}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <Users size={16} className="text-gray-500" />
                            Profissional
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loadingUsuarios}
                                >
                                    <div className="flex items-center gap-2">
                                        {loadingUsuarios ? (
                                            <>
                                                <LoadingIndicator size="sm" />
                                                <span className="text-gray-500">Carregando...</span>
                                            </>
                                        ) : (() => {
                                            const usuarioSelecionado = usuarios?.find(u => String(u.id) === String(formData.usuario_id));
                                            
                                            if (!usuarioSelecionado) {
                                                return (
                                                    <>
                                                        <UserX size={20} className="text-gray-400" />
                                                        <span className="text-gray-700">
                                                            Selecione um profissional...
                                                        </span>
                                                    </>
                                                );
                                            }
                                            
                                            const temFoto = usuarioSelecionado.fotoURL;
                                            const fotoUrl = temFoto ? getUsuarioFotoUrl(usuarioSelecionado.fotoURL) : null;
                                            
                                            return (
                                                <>
                                                    {temFoto && fotoUrl ? (
                                                        <img
                                                            src={fotoUrl}
                                                            alt={usuarioSelecionado.nome}
                                                            className="h-6 w-6 rounded-full object-cover border border-gray-200 flex-shrink-0"
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                const iconElement = e.target.parentElement.querySelector('.fallback-icon');
                                                                if (iconElement) iconElement.style.display = 'block';
                                                            }}
                                                        />
                                                    ) : null}
                                                    <UserCircle 
                                                        size={20} 
                                                        className="text-gray-400 fallback-icon" 
                                                        style={{ display: (temFoto && fotoUrl) ? 'none' : 'block' }} 
                                                    />
                                                    <span className="text-gray-700">
                                                        {usuarioSelecionado.nome}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={[
                                { 
                                    id: 'none', 
                                    label: '(Nenhum Profissional Atribuído)', 
                                    icon: UserX,
                                    iconColor: 'text-gray-400',
                                    onClick: () => handleDropdownChange('usuario_id', '')
                                },
                                ...(usuarios?.map((usuario) => {
                                    const temFoto = usuario.fotoURL;
                                    const fotoUrl = temFoto ? getUsuarioFotoUrl(usuario.fotoURL) : null;
                                    return {
                                        id: String(usuario.id),
                                        label: usuario.nome,
                                        ...(temFoto && fotoUrl ? { image: fotoUrl, imageAlt: usuario.nome } : { icon: UserCircle, iconColor: 'text-gray-400' }),
                                        onClick: () => handleDropdownChange('usuario_id', String(usuario.id))
                                    };
                                }) || [])
                            ]}
                            position="bottom-left"
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Cliente/Fornecedor e Documento */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Cliente
                        </label>
                        <input
                            type="text"
                            name="cliente_fornecedor"
                            value={formData.cliente_fornecedor}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="Nome do cliente"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            CPF/CNPJ
                        </label>
                        <input
                            type="text"
                            name="documento"
                            value={formData.documento}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            placeholder="000.000.000-00 ou 00.000.000/0000-00"
                        />
                    </div>
                </div>

                {/* Status (apenas ao editar) - Usando DropdownMenu */}
                {conta && (
                    <div>
                        <label className="block text-gray-600 font-medium mb-1 flex items-center gap-2">
                            <CheckCircle size={16} className="text-gray-500" />
                            Status
                        </label>
                        <DropdownMenu
                            trigger={
                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="w-full flex items-center justify-between gap-2 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={conta.status === 'Recebido'}
                                >
                                    <div className="flex items-center gap-2">
                                        {(() => {
                                            const statusSelecionado = STATUS_CONTA.find(s => s.value === formData.status);
                                            const Icon = statusSelecionado?.icon || CheckCircle;
                                            return (
                                                <>
                                                    <Icon size={16} className="text-gray-500" />
                                                    <span className="text-gray-700">
                                                        {statusSelecionado?.label || "Selecione o status"}
                                                    </span>
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </button>
                            }
                            items={STATUS_CONTA.map(s => ({
                                id: s.value,
                                label: s.label,
                                icon: s.icon,
                                iconColor: 'text-gray-600',
                                onClick: () => handleDropdownChange('status', s.value),
                                disabled: conta.status === 'Recebido'
                            }))}
                            position="bottom-left"
                            className="w-full"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            {conta.status === 'Recebido' 
                                ? 'Não é possível alterar o status de uma conta já recebida.' 
                                : 'Altere o status para reativar uma conta cancelada.'}
                        </p>
                    </div>
                )}

                {/* Observações */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Observações
                    </label>
                    <textarea
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Observações adicionais..."
                    />
                </div>

                {/* Erro de Submit */}
                {errors.submit && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{errors.submit}</p>
                    </div>
                )}

                {/* Botões */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        disabled={isSubmitting}
                    >
                        Cancelar
                    </button>
                    <PrimaryButton
                        type="submit"
                        disabled={isSubmitting}
                        className="px-4 py-2"
                    >
                        {isSubmitting ? 'Salvando...' : conta ? 'Atualizar' : 'Criar'}
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

export default FormContaReceber;
